import React, { useState } from 'react';
import {
  BadgeDollarSign, CalendarDays, CheckSquare2, ChevronLeft, DollarSign, FolderKanban, Inbox,
  LayoutDashboard, LockKeyhole, LogOut, Mail, MessagesSquare, Search, Settings, ShieldCheck, Users, X,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context';
import { UserIdentity } from '../People/PeopleUI';
import logo from '../../images/favicon.png';

const NAV_ITEMS = [
  { id: 'my-work', label: 'My Work', to: '/portal', icon: LayoutDashboard, roles: ['admin', 'project_manager', 'production'], exact: true },
  { id: 'projects', label: 'Projects', to: '/portal/projects', icon: FolderKanban, roles: ['admin', 'project_manager', 'production'] },
  { id: 'clients', label: 'Clients', to: '/portal/clients', icon: Users, roles: ['admin', 'project_manager', 'production'], match: /^\/portal\/clients/ },
  { id: 'tasks', label: 'My Tasks', to: '/portal/tasks', icon: CheckSquare2, roles: ['admin', 'project_manager', 'production'] },
  { id: 'messages', label: 'Messages', to: '/portal/messages', icon: Inbox, roles: ['admin', 'project_manager', 'production'] },
  { id: 'pings', label: 'Pings', to: '/portal/pings', icon: MessagesSquare, roles: ['admin', 'project_manager', 'production'] },
  { id: 'sales', label: 'Sales', to: '/sales', icon: BadgeDollarSign, roles: ['admin', 'project_manager'] },
  { id: 'finances', label: 'Finances', to: '/finances', icon: DollarSign, roles: ['admin'] },
  { id: 'calendar', label: 'Calendar', to: '/calendar', icon: CalendarDays, roles: ['admin'] },
  { id: 'mailer', label: 'Mailer', to: '/mailer', icon: Mail, roles: ['admin'] },
  { id: 'private-space', label: 'Private Space', to: '/portal/private-space', icon: LockKeyhole, roles: ['admin', 'project_manager', 'production'] },
  { id: 'manager', label: 'Team', to: '/portal/manager', icon: ShieldCheck, roles: ['admin'] },
];

export default function Sidebar({ collapsed = false, open = false, onClose, onToggleCollapse, onSettings, logout, clientName }) {
  const { user } = useAuth();
  const role = user?.role || 'production';
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const visibleItems = NAV_ITEMS.filter((item) => item.roles.includes(role));
  const primaryItems = visibleItems.slice(0, 5);
  const workspaceItems = visibleItems.slice(5);

  const isActive = (item) => item.match ? item.match.test(location.pathname) : item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
  const go = (to) => { navigate(to); onClose?.(); };
  const submitSearch = (event) => { event.preventDefault(); const next = query.trim(); go(next ? `/portal/projects?q=${encodeURIComponent(next)}` : '/portal/projects'); };

  const renderItems = (items) => items.map((item) => {
    const Icon = item.icon; const active = isActive(item);
    return <button key={item.id} type="button" className={`sidebar-nav-item ${active ? 'active' : ''}`} onClick={() => go(item.to)} aria-current={active ? 'page' : undefined} title={collapsed ? item.label : undefined}><Icon size={17} /><span>{item.label}</span></button>;
  });

  return <>
    <aside className={`app-sidebar ${collapsed ? 'collapsed' : ''} ${open ? 'open' : ''}`} aria-label="Workspace navigation">
      <div className="sidebar-brand-row">
        <button type="button" className="sidebar-brand" onClick={() => go('/portal')} aria-label="Evercrest workspace home"><img src={logo} alt="" /><span><strong>Evercrest</strong><small>Publishing workspace</small></span></button>
        <button type="button" className="ui-icon-button sidebar-mobile-close" onClick={onClose} aria-label="Close navigation"><X size={18} /></button>
      </div>
      <form className="sidebar-search" onSubmit={submitSearch} role="search"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Quick find" aria-label="Quick find" /></form>
      <nav className="sidebar-primary-nav" aria-label="Primary">
        <span className="sidebar-group-label">Workspace</span>{renderItems(primaryItems)}
        <span className="sidebar-group-label">Tools</span>{renderItems(workspaceItems)}
      </nav>
      <div className="sidebar-account">
        <button type="button" className="sidebar-nav-item sidebar-settings-link" onClick={onSettings} title={collapsed ? 'Settings' : undefined}><Settings size={17} /><span>Settings</span></button>
        <div className="sidebar-user-row"><UserIdentity person={user} name={clientName} size="sm" showRole className="sidebar-current-user" /><button type="button" className="ui-icon-button sidebar-signout" onClick={logout} aria-label="Sign out" title="Sign out"><LogOut size={16} /></button></div>
      </div>
      <button type="button" className="sidebar-collapse-button" onClick={onToggleCollapse} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}><ChevronLeft size={16} /></button>
    </aside>
    <button type="button" className={`sidebar-backdrop ${open ? 'visible' : ''}`} onClick={onClose} aria-label="Close navigation" tabIndex={open ? 0 : -1} />
  </>;
}
